import React from 'react';
import styled from 'styled-components';
import { styles } from '../../../../styles/themes/style';
import { ISequenceStep, SequenceStepType, DEFAULT_AI_LANGUAGE, NOTE_LIMIT_FALLBACK_OPTIONS, SN_NON_OPEN_FALLBACK_OPTIONS, SN_INMAIL_CREDITS_HOWTO, CONNECTION_REQUEST_NOTE_MAX_CHARS, CONNECTION_REQUEST_NOTES_MONTHLY_FREE, ISenderIdentity } from '../../../../interfaces/models/campaign';
import Column from '../../../../components/Column';
import SvgFragment from '../../../../fragments/SvgFragment';
import { applyDescriptionToSequence } from '../../utils/campaignAiPrompts';
import TooltipWrapper from '../../../../components/TooltipWrapper';
import { campaignMobile } from '../../utils/campaignBreakpoints';
import CampaignContentField from './CampaignContentField';
import CampaignSnMessageComposer from './CampaignSnMessageComposer';
import { languagesForStep } from './CampaignLanguageDropdown';
import CampaignEmailComposer from './CampaignEmailComposer';
import {
  CampaignVariableKey,
  ICampaignVariableFallbacks,
} from '../../../../utils/campaignTemplateVariables';
import { hasPaidInMailCredits, InMailCreditSummary } from '../../utils/inMailCredits';

// ─── Step metadata ────────────────────────────────────────────────────────────

const STEP_META: Record<SequenceStepType, { label: string; icon: string; hint: string }> = {
  connection_request: { label: 'Connection Request',      icon: 'stepConnect', hint: 'Send a LinkedIn connection request (optional personalized note)' },
  message:            { label: 'Direct Message',          icon: 'stepMessage', hint: 'Send a message after connecting'   },
  sn_message:         { label: 'InMail / Open Profile', icon: 'stepMessage', hint: 'Send Open Profile or InMail messages to non-connections' },
  profile_visit:      { label: 'Profile Visit',           icon: 'stepVisit',   hint: 'View the prospect’s LinkedIn profile' },
  post_like:          { label: 'Post Like',               icon: 'stepLike',    hint: "Like the prospect's latest post"  },
  post_comment:       { label: 'Post Comment',            icon: 'stepComment', hint: "Comment on the prospect's post"   },
  email:              { label: 'Email',               icon: 'stepEmail',   hint: "Email the prospect from the sender's own mailbox" },
};

const STEP_TYPES = Object.keys(STEP_META) as SequenceStepType[];

/** Max occurrences of each step type in one campaign sequence. */
const MAX_STEPS_PER_TYPE: Record<SequenceStepType, number> = {
  connection_request: 1,
  message: 2,
  sn_message: 1,
  profile_visit: 1,
  post_like: 2,
  post_comment: 2,
  email: 3,
};

function countStepType(steps: ISequenceStep[], type: SequenceStepType): number {
  return steps.filter(s => s.type === type).length;
}

function canAddStepType(steps: ISequenceStep[], type: SequenceStepType): boolean {
  return countStepType(steps, type) < MAX_STEPS_PER_TYPE[type];
}

function maxStepsTooltip(type: SequenceStepType): string {
  const max = MAX_STEPS_PER_TYPE[type];
  const label = STEP_META[type].label.toLowerCase();
  return max === 1
    ? `Only one ${label} per campaign`
    : `Maximum ${max} ${label} steps per campaign`;
}

export interface ITemplate { id: string; label: string; description: string; steps: ISequenceStep[] }

export const SEQUENCE_TEMPLATES: ITemplate[] = [
  {
    id: 'empty',
    label: 'Empty',
    description: 'Start with no steps — add only what you need',
    steps: [],
  },
  {
    id: 'connect_message',
    label: 'Connect + Message',
    description: 'Standard outreach flow',
    steps: [
      { type: 'connection_request', delayDays: 0 },
      { type: 'message', delayDays: 1, messageMode: 'template', messageTemplate: '', messageAiPrompt: '', aiLanguage: DEFAULT_AI_LANGUAGE },
    ],
  },
  {
    id: 'warm_connect',
    label: 'Warm Up First',
    description: 'Like post, then connect & message',
    steps: [
      { type: 'post_like', delayDays: 0 },
      { type: 'connection_request', delayDays: 1 },
      { type: 'message', delayDays: 2, messageMode: 'template', messageTemplate: '', messageAiPrompt: '', aiLanguage: DEFAULT_AI_LANGUAGE },
    ],
  },
  {
    id: 'full_engage',
    label: 'Full Engagement',
    description: 'Like + comment before connecting',
    steps: [
      { type: 'post_like', delayDays: 0 },
      { type: 'post_comment', delayDays: 0, commentMode: 'template', commentTemplate: '', commentAiPrompt: '', aiLanguage: DEFAULT_AI_LANGUAGE },
      { type: 'connection_request', delayDays: 1 },
      { type: 'message', delayDays: 2, messageMode: 'template', messageTemplate: '', messageAiPrompt: '', aiLanguage: DEFAULT_AI_LANGUAGE },
    ],
  },
  {
    id: 'connect_email',
    label: 'LinkedIn + Email',
    description: 'Invite, email, then message once connected',
    /**
     * The email deliberately comes BEFORE the message.
     *
     * A message step waits for the invite to be accepted, and gives up on the lead
     * after the acceptance window. Putting email last would mean the people it
     * exists to reach — the ones who never accept — are exactly the ones who never
     * get it. Email has no acceptance gate, so placed here it goes out either way,
     * and the message still lands for whoever did connect.
     */
    steps: [
      { type: 'connection_request', delayDays: 0 },
      {
        type: 'email', delayDays: 3,
        emailSubjectMode: 'template', emailSubjectTemplate: '', emailSubjectAiPrompt: '',
        emailBodyMode: 'template', emailBodyTemplate: '', emailBodyAiPrompt: '',
        emailPreference: 'business', ifNoEmail: 'skip', replyInThread: true,
        aiLanguage: DEFAULT_AI_LANGUAGE,
      },
      { type: 'message', delayDays: 5, messageMode: 'template', messageTemplate: '', messageAiPrompt: '', aiLanguage: DEFAULT_AI_LANGUAGE },
    ],
  },
  {
    id: 'connect_only',
    label: 'Connect Only',
    description: 'Connection request, no follow-up',
    steps: [
      { type: 'connection_request', delayDays: 0 },
    ],
  },
  {
    id: 'sn_message',
    label: 'InMail / Open Profile',
    description: 'Message non-connections — Open Profile free, InMail uses credits',
    steps: [
      {
        type: 'sn_message',
        delayDays: 0,
        messageMode: 'template',
        messageTemplate: '',
        messageAiPrompt: '',
        messageSubject: '',
        snNonOpenFallback: 'open_only',
        aiLanguage: DEFAULT_AI_LANGUAGE,
      },
    ],
  },
  {
    id: 'profile_visit',
    label: 'Profile Visit',
    description: 'View the profile — add connect or a message after',
    steps: [
      { type: 'profile_visit', delayDays: 0 },
    ],
  },
];

function matchesTemplate(steps: ISequenceStep[]): string {
  if (!steps.length) return 'empty';
  const t = SEQUENCE_TEMPLATES.find(
    t => t.steps.length > 0 && t.steps.length === steps.length && t.steps.every((s, i) => s.type === steps[i]?.type)
  );
  return t?.id ?? 'custom';
}

function isTemplateActive(templateId: string, matched: string): boolean {
  if (templateId === 'empty') return matched === 'empty' || matched === 'custom';
  return matched === templateId;
}

/*
  New steps start in 'template' mode with an empty field.

  'ai' is now only ever a legacy value: it means the step writes a fresh message for
  every lead at send time, which is what campaigns created before the composer was
  reworked still do. Creating new steps that way would put the "still writes per lead"
  warning on a step the user had just added, and — worse — a step saved without
  pressing Generate would send its own instruction as the message.

  The campaign description is not pre-filled into the field any more either. It is
  passed to the generator as context regardless, so nothing is lost, and an empty
  field is what makes the placeholder ("write, or describe what you want") true.
*/
function newStep(type: SequenceStepType, prevDelay = 0): ISequenceStep {
  return {
    type,
    delayDays: prevDelay + 1,
    ...(type === 'message' && { messageMode: 'template', messageTemplate: '', messageAiPrompt: '', aiLanguage: DEFAULT_AI_LANGUAGE }),
    ...(type === 'sn_message' && {
      messageMode: 'template',
      messageTemplate: '',
      messageAiPrompt: '',
      messageSubject: '',
      snNonOpenFallback: 'open_only' as const,
      aiLanguage: DEFAULT_AI_LANGUAGE,
    }),
    ...(type === 'post_comment' && { commentMode: 'template', commentTemplate: '', commentAiPrompt: '', aiLanguage: DEFAULT_AI_LANGUAGE }),
    ...(type === 'email' && {
      emailSubjectMode: 'template', emailSubjectTemplate: '', emailSubjectAiPrompt: '',
      emailBodyMode: 'template', emailBodyTemplate: '', emailBodyAiPrompt: '',
      aiLanguage: DEFAULT_AI_LANGUAGE,
      // Work address only, and don't kill a lead over a missing one — both are the
      // safe defaults, and both are opt-out rather than opt-in for a reason.
      emailPreference: 'business', ifNoEmail: 'skip', replyInThread: true,
    }),
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  value: ISequenceStep[];
  /** Prefer functional updates — preview/AI effects can race with note toggles otherwise. */
  onChange: (steps: ISequenceStep[] | ((prev: ISequenceStep[]) => ISequenceStep[])) => void;
  readOnly?: boolean;
  /**
   * Paused campaign: message text stays editable, the steps themselves do not.
   * Add, remove, reorder, delays, and send rules stay as they were at start.
   */
  textOnly?: boolean;
  /** Campaign description — used to pre-fill empty AI prompts. */
  campaignDescription?: string;
  variableFallbacks?: ICampaignVariableFallbacks;
  onVariableFallbackChange?: (key: CampaignVariableKey, value: string, saveAsUserDefault?: boolean) => void;
  /** Senders this campaign sends as — the email step shows whose signature it appends. */
  senderIdentities?: ISenderIdentity[];
  /** Campaign footer settings, edited from inside the email step. */
  emailConfig?: { physicalAddress?: string; footer?: string };
  onEmailConfigChange?: (patch: { physicalAddress?: string; footer?: string }) => void;
  /**
   * Paint required-but-empty fields as invalid.
   *
   * Off until the wizard refuses to advance. A campaign opened for editing should not
   * accuse the user of missing text before asking for it.
   */
  showErrors?: boolean;
  /**
   * Proxy InMail credit summary for the campaign's connected accounts.
   * `null`/`undefined` = unknown (extension campaign, or accounts not loaded yet).
   * When known and zero, "Use InMail credits" is disabled — Open Profile only stays available.
   */
  inMailCreditSummary?: InMailCreditSummary | null;
}

const CampaignSequenceBuilder: React.FC<Props> = ({
  value,
  onChange,
  readOnly = false,
  textOnly = false,
  campaignDescription = '',
  variableFallbacks = {},
  onVariableFallbackChange,
  senderIdentities = [],
  emailConfig,
  onEmailConfigChange,
  showErrors = false,
  inMailCreditSummary = null,
}) => {
  const handleFallbackChange = (key: CampaignVariableKey, val: string, saveAsUserDefault?: boolean) => {
    onVariableFallbackChange?.(key, val, saveAsUserDefault);
  };

  const paidInMailOk = hasPaidInMailCredits(inMailCreditSummary);
  const paidInMailDisabled = paidInMailOk === false;
  // Active and stopped campaigns are fully locked. A paused one keeps the text
  // fields and locks everything that would move a lead onto a different step.
  const structureLocked = readOnly || textOnly;

  // No credits on connected accounts → paid InMail is not selectable; keep Open Profile only.
  React.useEffect(() => {
    if (!paidInMailDisabled || structureLocked) return;
    onChange(prev => {
      if (!prev.some(s => s.type === 'sn_message' && s.snNonOpenFallback === 'inmail_if_credits')) {
        return prev;
      }
      return prev.map(s => (
        s.type === 'sn_message' && s.snNonOpenFallback === 'inmail_if_credits'
          ? { ...s, snNonOpenFallback: 'open_only' as const }
          : s
      ));
    });
  }, [paidInMailDisabled, structureLocked, onChange]);

  /** Name drawn at the top of a generated preview, so it reads as a real message. */
  const previewSenderName = senderIdentities[0]?.name
    || senderIdentities[0]?.linkedin?.name
    || undefined;
  const activeTemplate = matchesTemplate(value);
  const dragFromRef = React.useRef<number | null>(null);
  const [dayDrafts, setDayDrafts] = React.useState<Record<number, string>>({});
  const [draggingIdx, setDraggingIdx] = React.useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = React.useState<number | null>(null);

  const clearDayDrafts = React.useCallback(() => setDayDrafts({}), []);

  const update = (idx: number, patch: Partial<ISequenceStep>) =>
    onChange(prev => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

  const remove = (idx: number) => {
    clearDayDrafts();
    onChange(prev => prev.filter((_, i) => i !== idx));
  };

  const reorder = (from: number, to: number) => {
    if (from === to) return;
    onChange(prev => {
      if (from < 0 || to < 0 || from >= prev.length || to >= prev.length) return prev;
      const days = prev.map(s => s.delayDays);
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next.map((step, i) => ({ ...step, delayDays: days[i] }));
    });
    clearDayDrafts();
    setDragOverIdx(null);
  };

  const applyDescription = (steps: ISequenceStep[]) =>
    campaignDescription.trim() ? applyDescriptionToSequence(campaignDescription, steps) : steps;

  const applyTemplate = (id: string) => {
    const t = SEQUENCE_TEMPLATES.find(t => t.id === id);
    if (t) {
      clearDayDrafts();
      onChange(applyDescription(t.steps.map(s => ({ ...s }))));
    }
  };

  const addStep = (type: SequenceStepType) => {
    onChange(prev => {
      if (!canAddStepType(prev, type)) return prev;
      if (!prev.length) {
        return applyDescription([{ ...newStep(type, -1) }]);
      }
      const lastDelay = prev[prev.length - 1]?.delayDays ?? 0;
      return applyDescription([...prev, newStep(type, lastDelay)]);
    });
    clearDayDrafts();
  };

  const terminalStepNumber = value.length + 1;
  const hasMessageStep = value.some(s => s.type === 'message' || s.type === 'sn_message');

  const addStepButtons = (
    <>
      {STEP_TYPES.map(type => {
        const disabled = !canAddStepType(value, type);
        return (
          <TooltipWrapper
            key={type}
            tooltipText={disabled ? maxStepsTooltip(type) : ''}
            placement="top"
          >
            <AddBtnWrap>
              <AddBtn
                type="button"
                disabled={disabled}
                onClick={() => addStep(type)}
              >
                <SvgFragment type={STEP_META[type].icon} />{STEP_META[type].label}
              </AddBtn>
            </AddBtnWrap>
          </TooltipWrapper>
        );
      })}
    </>
  );

  return (
    <Wrap>
      {/*
        No standing "Set fallbacks for…" banner.

        It carried the same imperative as the blocker that used to refuse the step, so
        removing the refusal while leaving this would have kept the nagging and only
        dropped the enforcement. The chips under each message field say the same thing
        where it can actually be acted on, next to the variable it concerns.
      */}
      {/* ── Template picker ── */}
      {!structureLocked && (
        <TemplateGrid>
          {SEQUENCE_TEMPLATES.map(t => {
            const active = isTemplateActive(t.id, activeTemplate);
            return (
            <TemplateCard key={t.id} type="button" $active={active} onClick={() => applyTemplate(t.id)}>
              <TplPipeline>
                {t.steps.length === 0 ? (
                  <TplEmptyIcon>+</TplEmptyIcon>
                ) : t.steps.map((s, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <TplArrow>→</TplArrow>}
                    <TplIcon title={STEP_META[s.type].label}><SvgFragment type={STEP_META[s.type].icon} /></TplIcon>
                  </React.Fragment>
                ))}
              </TplPipeline>
              <TplName $active={active}>{t.label}</TplName>
              <TplDesc>{t.description}</TplDesc>
            </TemplateCard>
            );
          })}
        </TemplateGrid>
      )}

      {/* ── Timeline ── */}
      <TimelinePanel>
        {value.map((step, idx) => {
          const meta = STEP_META[step.type];
          const isDragging = draggingIdx === idx;
          const isDropTarget = draggingIdx != null && dragOverIdx === idx && draggingIdx !== idx;
          return (
            <TimelineRow
              key={idx}
              onDragOver={e => {
                if (structureLocked) return;
                e.preventDefault();
                try { e.dataTransfer.dropEffect = 'move'; } catch (_) { /* best-effort */ }
                if (draggingIdx != null && draggingIdx !== idx) setDragOverIdx(idx);
              }}
              onDragLeave={() => {
                if (dragOverIdx === idx) setDragOverIdx(null);
              }}
              onDrop={e => {
                if (structureLocked) return;
                e.preventDefault();
                const raw = e.dataTransfer.getData('text/plain');
                const from = (dragFromRef.current ?? (raw ? parseInt(raw, 10) : NaN));
                if (!Number.isFinite(from)) return;
                reorder(from as number, idx);
                dragFromRef.current = null;
                setDraggingIdx(null);
                setDragOverIdx(null);
              }}
            >
              <Rail>
                <StepBadge>{idx + 1}</StepBadge>
                <RailLine />
              </Rail>

              <StepCard $dragging={isDragging} $dropTarget={isDropTarget}>
                <StepHead>
                  <StepHeadMain>
                    {!structureLocked && (
                      <TooltipWrapper tooltipText="Drag to reorder" placement="top">
                        <DragCursorWrap $dragging={draggingIdx === idx}>
                          <DragHandle
                            type="button"
                            draggable
                            $dragging={draggingIdx === idx}
                            onDragStart={e => {
                              dragFromRef.current = idx;
                              setDraggingIdx(idx);
                              setDragOverIdx(null);
                              try { e.dataTransfer.setData('text/plain', String(idx)); } catch (_) { /* best-effort */ }
                              e.dataTransfer.effectAllowed = 'move';
                            }}
                            onDragEnd={() => {
                              dragFromRef.current = null;
                              setDraggingIdx(null);
                              setDragOverIdx(null);
                            }}
                          >
                            <SvgFragment type="gripVerticalLucide" />
                          </DragHandle>
                        </DragCursorWrap>
                      </TooltipWrapper>
                    )}
                    <StepIcon><SvgFragment type={meta.icon} /></StepIcon>
                    <Column gap="2px" style={{ minWidth: 0 }}>
                      <StepLabel>{meta.label}</StepLabel>
                      <StepHint>{meta.hint}</StepHint>
                    </Column>
                  </StepHeadMain>

                  {!structureLocked ? (
                    <StepHeadActions>
                      <DayBox>
                        <DayLbl>Day</DayLbl>
                        <DayInput
                          type="number"
                          min={0}
                          value={dayDrafts[idx] ?? String(step.delayDays)}
                          onChange={e => setDayDrafts(d => ({ ...d, [idx]: e.target.value }))}
                          onBlur={() => {
                            const raw = dayDrafts[idx];
                            const parsed = raw == null ? step.delayDays : (raw.trim() === '' ? 0 : Number(raw));
                            const nextDelay = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
                            setDayDrafts(d => {
                              const next = { ...d };
                              delete next[idx];
                              return next;
                            });
                            update(idx, { delayDays: nextDelay });
                          }}
                        />
                      </DayBox>
                      <TooltipWrapper tooltipText="Remove step" placement="top">
                        <IconBtn type="button" $danger onClick={() => remove(idx)}>
                          <SvgFragment type="trash2Lucide" />
                        </IconBtn>
                      </TooltipWrapper>
                    </StepHeadActions>
                  ) : (
                    <DayTag>Day {step.delayDays}</DayTag>
                  )}
                </StepHead>

                {/* Connection request note config */}
                {step.type === 'connection_request' && (() => {
                  const legacy = step.messageMode === 'ai' && !(step.messageTemplate ?? '').trim();
                  const text = (step.messageTemplate ?? '') || (legacy ? (step.messageAiPrompt ?? '') : '');
                  return (
                  <StepBody>
                    {!structureLocked && (
                      <>
                        {/* Two states, not three: the note is either there or it is
                            not. How its text was written is no longer a stored mode. */}
                        <ModeRow>
                          <ModeBtn
                            type="button"
                            $active={!step.includeNote}
                            onClick={() => update(idx, {
                              includeNote: false,
                              messageMode: 'template',
                              messageTemplate: '',
                              messageAiPrompt: '',
                              finalContent: '',
                              noteLimitFallback: step.noteLimitFallback || 'without_note',
                            })}
                          >
                            No note
                          </ModeBtn>
                          <ModeBtn
                            type="button"
                            $active={!!step.includeNote}
                            onClick={() => update(idx, {
                              includeNote: true,
                              aiLanguage: step.aiLanguage || DEFAULT_AI_LANGUAGE,
                              noteLimitFallback: step.noteLimitFallback || 'without_note',
                            })}
                          >
                            With a note
                          </ModeBtn>
                        </ModeRow>
                      </>
                    )}

                        {step.includeNote && !readOnly && (
                          <>
                            <CampaignContentField
                              label="Connection note"
                              stepType="connection_request"
                              languageStepType="connection_request"
                              value={text}
                              legacyAi={legacy}
                              maxLength={CONNECTION_REQUEST_NOTE_MAX_CHARS}
                              placeholder={'Write your note, or describe what you want AI to generate.\n\ne.g. Hi {{firstName}}, I came across your work at {{company}} and would love to connect.'}
                              rows={3}
                              /* No template picker: library entries are full messages
                                 and would be cut off at 300 characters. */
                              templateLibrary={false}
                              generateLabel="Generate note"
                              onChange={val => update(idx, {
                                messageTemplate: val, messageMode: 'template', finalContent: '',
                              })}
                              language={step.aiLanguage || DEFAULT_AI_LANGUAGE}
                              onLanguageChange={lang => update(idx, { aiLanguage: lang })}
                              fallbacks={variableFallbacks}
                              onFallbackChange={handleFallbackChange}
                              campaignDescription={campaignDescription}
                              senderName={previewSenderName}
                            />

                            {!structureLocked && (
                            <NoteLimitBox>
                              <NoteLimitTitle>
                                Free LinkedIn accounts: max {CONNECTION_REQUEST_NOTES_MONTHLY_FREE} notes / month
                              </NoteLimitTitle>
                              <NoteLimitText>
                                Premium / Sales Navigator are not limited by this monthly note quota.
                                When the free quota is reached:
                              </NoteLimitText>
                              <FallbackRow>
                                {NOTE_LIMIT_FALLBACK_OPTIONS.map(opt => {
                                  const activeFallback = step.noteLimitFallback === 'pause_noted'
                                    ? 'pause_noted'
                                    : 'without_note';
                                  return (
                                    <TooltipWrapper key={opt.value} tooltipText={opt.hint} placement="top">
                                      <FallbackBtn
                                        type="button"
                                        $active={activeFallback === opt.value}
                                        onClick={() => update(idx, { noteLimitFallback: opt.value })}
                                      >
                                        {opt.label}
                                      </FallbackBtn>
                                    </TooltipWrapper>
                                  );
                                })}
                              </FallbackRow>
                            </NoteLimitBox>
                            )}
                          </>
                        )}
                    {(readOnly || (textOnly && !step.includeNote)) && (
                      <ReadOnly>
                        {step.includeNote
                          ? (step.finalContent || text || <em style={{ color: '#c4cdd8' }}>Note enabled</em>)
                          : <em style={{ color: '#c4cdd8' }}>No connection note</em>}
                      </ReadOnly>
                    )}
                  </StepBody>
                  );
                })()}

                {/* Message config */}
                {step.type === 'message' && (() => {
                  const legacy = step.messageMode === 'ai' && !(step.messageTemplate ?? '').trim();
                  const text = (step.messageTemplate ?? '') || (legacy ? (step.messageAiPrompt ?? '') : '');
                  return (
                  <StepBody>
                    {!readOnly && (
                      <CampaignContentField
                        label="Message"
                        required
                        stepType="message"
                        languageStepType="message"
                        value={text}
                        legacyAi={legacy}
                        invalid={showErrors && !text.trim()}
                        placeholder={'Write your message, or describe what you want AI to generate.\n\ne.g. Hi {{firstName}}, I noticed you work at {{company}} as {{title}} — I\'d love to connect.'}
                        rows={5}
                        /* Writing here fixes the wording for everyone. finalContent is
                           cleared with it: the executor reads that first, so a draft
                           frozen by the old flow would outrank what is on screen. */
                        onChange={val => update(idx, {
                          messageTemplate: val, messageMode: 'template', finalContent: '',
                        })}
                        language={step.aiLanguage || DEFAULT_AI_LANGUAGE}
                        onLanguageChange={lang => update(idx, { aiLanguage: lang })}
                        fallbacks={variableFallbacks}
                        onFallbackChange={handleFallbackChange}
                        campaignDescription={campaignDescription}
                        senderName={previewSenderName}
                      />
                    )}
                    {readOnly && (
                      <ReadOnly>
                        {legacy && (
                          <ReadOnlyAiMeta>
                            <span>Written per lead from this instruction</span>
                            {step.aiLanguage && step.aiLanguage !== DEFAULT_AI_LANGUAGE && (
                              <LangBadge>
                                {languagesForStep('message').find(l => l.code === step.aiLanguage)?.flag ?? '🌐'}{' '}
                                {languagesForStep('message').find(l => l.code === step.aiLanguage)?.label ?? step.aiLanguage}
                              </LangBadge>
                            )}
                          </ReadOnlyAiMeta>
                        )}
                        {text || <em style={{ color: '#c4cdd8' }}>No message configured</em>}
                      </ReadOnly>
                    )}
                  </StepBody>
                  );
                })()}

                {/* Email config */}
                {step.type === 'email' && (() => {
                  // A follow-up that replies in-thread inherits the original subject,
                  // so only the first email in the sequence has to define one.
                  const isFirstEmail = value.findIndex(s => s.type === 'email') === idx;
                  const needsSubject = isFirstEmail || step.replyInThread === false;
                  const bodyValue = (step.emailBodyMode === 'ai' ? step.emailBodyAiPrompt : step.emailBodyTemplate) ?? '';
                  const subjectValue = (step.emailSubjectMode === 'ai' ? step.emailSubjectAiPrompt : step.emailSubjectTemplate) ?? '';

                  return (
                    <StepBody>
                      {!structureLocked && !isFirstEmail && (
                        <ThreadRow>
                          <ThreadToggle
                            type="button"
                            $active={step.replyInThread !== false}
                            onClick={() => update(idx, { replyInThread: step.replyInThread === false })}
                          >
                            {/* 'checkmark' is the bare tick — 'check' is a filled
                                purple disc, which inside this box renders as a
                                circle overflowing the square. */}
                            <ThreadBox $active={step.replyInThread !== false}>
                              {step.replyInThread !== false && <SvgFragment type="checkmark" />}
                            </ThreadBox>
                            Reply in the same thread
                          </ThreadToggle>
                          <FieldHint>
                            Keeps the follow-up in the original conversation — better deliverability, and it
                            reads like a person following up rather than a new blast.
                          </FieldHint>
                        </ThreadRow>
                      )}

                      <CampaignEmailComposer
                        showErrors={showErrors}
                        step={step}
                        onChange={patch => update(idx, patch)}
                        fallbacks={variableFallbacks}
                        onFallbackChange={handleFallbackChange}
                        needsSubject={needsSubject}
                        campaignDescription={campaignDescription}
                        senderIdentities={senderIdentities}
                        emailConfig={emailConfig}
                        onEmailConfigChange={onEmailConfigChange}
                        readOnly={readOnly}
                        lockDeliveryOptions={textOnly}
                      />

                      {readOnly && (
                        <ReadOnly>
                          {needsSubject && subjectValue && <ReadOnlySubject>{subjectValue}</ReadOnlySubject>}
                          {step.emailBodyHtml && step.emailBodyMode !== 'ai'
                            // Stored markup, sanitized by the backend on save.
                            ? <div dangerouslySetInnerHTML={{ __html: bodyValue }} />
                            : (bodyValue || <em style={{ color: '#c4cdd8' }}>No email configured</em>)}
                        </ReadOnly>
                      )}
                    </StepBody>
                  );
                })()}

                {/* InMail / Open Profile config */}
                {step.type === 'sn_message' && (() => {
                  const legacy = step.messageMode === 'ai' && !(step.messageTemplate ?? '').trim();
                  const text = (step.messageTemplate ?? '') || (legacy ? (step.messageAiPrompt ?? '') : '');
                  return (
                  <StepBody>
                    {!structureLocked && (
                      <>
                        <ChoiceSection>
                          <ChoiceSectionTitle>
                            What should happen when the lead has a Closed Profile?
                          </ChoiceSectionTitle>
                          <ChoiceCards>
                            {SN_NON_OPEN_FALLBACK_OPTIONS.map(opt => {
                              const activeFallback = step.snNonOpenFallback === 'inmail_if_credits'
                                ? 'inmail_if_credits'
                                : 'open_only';
                              const active = activeFallback === opt.value;
                              const creditsLocked = opt.value === 'inmail_if_credits' && paidInMailDisabled;
                              const card = (
                                <ChoiceCard
                                  type="button"
                                  $active={active && !creditsLocked}
                                  $disabled={creditsLocked}
                                  disabled={creditsLocked}
                                  onClick={() => {
                                    if (creditsLocked) return;
                                    update(idx, { snNonOpenFallback: opt.value });
                                  }}
                                  aria-pressed={active && !creditsLocked}
                                  aria-disabled={creditsLocked}
                                >
                                  <ChoiceRadio $active={active && !creditsLocked} />
                                  <ChoiceCopy>
                                    <ChoiceLabel>{opt.label}</ChoiceLabel>
                                    <ChoiceHint>{opt.hint}</ChoiceHint>
                                  </ChoiceCopy>
                                </ChoiceCard>
                              );
                              if (!creditsLocked) {
                                return <React.Fragment key={opt.value}>{card}</React.Fragment>;
                              }
                              return (
                                <TooltipWrapper
                                  key={opt.value}
                                  tooltipText="No InMail credits on connected accounts"
                                  placement="top"
                                >
                                  {/* Wrapper so tooltip still works on a disabled button */}
                                  <span style={{ display: 'block', width: '100%' }}>{card}</span>
                                </TooltipWrapper>
                              );
                            })}
                          </ChoiceCards>
                          {step.snNonOpenFallback === 'inmail_if_credits' && !paidInMailDisabled && (
                            <ChoiceInfoNote>
                              <ChoiceInfoIcon aria-hidden>i</ChoiceInfoIcon>
                              <span>{SN_INMAIL_CREDITS_HOWTO}</span>
                            </ChoiceInfoNote>
                          )}
                          {paidInMailDisabled && (
                            <ChoiceInfoNote>
                              <ChoiceInfoIcon aria-hidden>i</ChoiceInfoIcon>
                              <span>
                                No InMail credits on connected accounts — closed profiles will be skipped.
                                Open Profile messages still send for free.
                              </span>
                            </ChoiceInfoNote>
                          )}
                        </ChoiceSection>
                      </>
                    )}
                    {textOnly && (
                      <ReadOnly>
                        {step.snNonOpenFallback === 'inmail_if_credits'
                          ? 'Use InMail credits'
                          : 'Open Profile only'}
                        {' — this choice stays as it was when the campaign started.'}
                      </ReadOnly>
                    )}
                    {!readOnly && (
                        <CampaignSnMessageComposer
                          step={step}
                          onChange={patch => update(idx, patch)}
                          fallbacks={variableFallbacks}
                          onFallbackChange={handleFallbackChange}
                          campaignDescription={campaignDescription}
                          senderName={previewSenderName}
                          showErrors={showErrors}
                        />
                    )}
                    {readOnly && (
                      <ReadOnly>
                        {step.messageSubject?.trim() && (
                          <div style={{ marginBottom: 6 }}>
                            <strong>Subject:</strong> {step.messageSubject}
                          </div>
                        )}
                        <div style={{ marginBottom: 6, color: '#64748b', fontSize: 12 }}>
                          {(step.snNonOpenFallback === 'inmail_if_credits'
                            ? 'Use InMail credits'
                            : 'Open Profile only')}
                        </div>
                        {legacy && (
                          <ReadOnlyAiMeta>
                            <span>Written per lead from this instruction</span>
                            {step.aiLanguage && step.aiLanguage !== DEFAULT_AI_LANGUAGE && (
                              <LangBadge>
                                {languagesForStep('sn_message').find(l => l.code === step.aiLanguage)?.flag ?? '🌐'}{' '}
                                {languagesForStep('sn_message').find(l => l.code === step.aiLanguage)?.label ?? step.aiLanguage}
                              </LangBadge>
                            )}
                          </ReadOnlyAiMeta>
                        )}
                        {text || <em style={{ color: '#c4cdd8' }}>No message configured</em>}
                      </ReadOnly>
                    )}
                  </StepBody>
                  );
                })()}

                {/* Comment config */}
                {step.type === 'post_comment' && (() => {
                  const legacy = step.commentMode === 'ai' && !(step.commentTemplate ?? '').trim();
                  const text = (step.commentTemplate ?? '') || (legacy ? (step.commentAiPrompt ?? '') : '');
                  return (
                  <StepBody>
                    {!readOnly && (
                      <CampaignContentField
                        label="Comment"
                        required
                        stepType="post_comment"
                        languageStepType="post_comment"
                        value={text}
                        legacyAi={legacy}
                        invalid={showErrors && !text.trim()}
                        placeholder={'Write your comment, or describe what you want AI to generate.\n\ne.g. A genuine, specific comment on {{firstName}}\'s post. Under 200 characters, no pitch.'}
                        rows={4}
                        onChange={val => update(idx, {
                          commentTemplate: val, commentMode: 'template', finalContent: '',
                        })}
                        language={step.aiLanguage || DEFAULT_AI_LANGUAGE}
                        onLanguageChange={lang => update(idx, { aiLanguage: lang })}
                        fallbacks={variableFallbacks}
                        onFallbackChange={handleFallbackChange}
                        campaignDescription={campaignDescription}
                        senderName={previewSenderName}
                        hint="Generating here works from the instruction alone — the lead's actual post is only available at send time."
                      />
                    )}
                    {readOnly && (
                      <ReadOnly>
                        {legacy && (
                          <ReadOnlyAiMeta>
                            <span>Written per lead from this instruction</span>
                            {step.aiLanguage && step.aiLanguage !== DEFAULT_AI_LANGUAGE && (
                              <LangBadge>
                                {languagesForStep('post_comment').find(l => l.code === step.aiLanguage)?.flag ?? '🌐'}{' '}
                                {languagesForStep('post_comment').find(l => l.code === step.aiLanguage)?.label ?? step.aiLanguage}
                              </LangBadge>
                            )}
                          </ReadOnlyAiMeta>
                        )}
                        {text || <em style={{ color: '#c4cdd8' }}>No comment configured</em>}
                      </ReadOnly>
                    )}
                  </StepBody>
                  );
                })()}
              </StepCard>
            </TimelineRow>
          );
        })}

        {!structureLocked && value.length === 0 && (
          <TimelineRow>
            <RailSpacer />
            <EmptyHint>
              <EmptySequenceTitle>No steps yet</EmptySequenceTitle>
              <EmptySequenceText>
                Add steps below — e.g. only <strong>Message</strong> for existing LD Contacts.
              </EmptySequenceText>
            </EmptyHint>
          </TimelineRow>
        )}

        {!structureLocked && (
          <TimelineRow>
            <RailSpacer />
            <AddRow>
              <AddLbl>Add step</AddLbl>
              <AddBtnsRow>{addStepButtons}</AddBtnsRow>
            </AddRow>
          </TimelineRow>
        )}

        {/* Terminal — read-only end marker (last step number, disabled style) */}
        {value.length > 0 && (
        <TimelineRow>
          <Rail>
            <StepBadge>{terminalStepNumber}</StepBadge>
          </Rail>
          <TerminalCard>
            <TerminalIcon><SvgFragment type="stepGoal" /></TerminalIcon>
            <Column gap="2px" style={{ minWidth: 0 }}>
              <TerminalTitle>End of sequence</TerminalTitle>
              <TerminalDesc>
                {hasMessageStep ? (
                  <>
                    Leads become <strong>Replied</strong> automatically when they message back, or you mark them{' '}
                    <strong>Converted</strong>, <strong>Closed Lost</strong>, or <strong>Failed</strong> on the Leads page.
                  </>
                ) : (
                  <>
                    When the sequence is done, mark leads <strong>Converted</strong>, <strong>Closed Lost</strong>, or{' '}
                    <strong>Failed</strong> on the Leads page.
                  </>
                )}
              </TerminalDesc>
            </Column>
          </TerminalCard>
        </TimelineRow>
        )}
      </TimelinePanel>
    </Wrap>
  );
};

// ─── Styled components ────────────────────────────────────────────────────────

const ACCENT = styles.colors.primary600;
const BORDER = '#e2e8f0';
const MUTED  = '#94a3b8';
const INK    = '#1e293b';

const Wrap = styled.div`display: flex; flex-direction: column; gap: 12px;`;

const TimelinePanel = styled.div`
  border: 1px solid ${BORDER}; border-radius: 12px; background: #fafbfc;
  padding: 14px 14px 10px; display: flex; flex-direction: column;
  ${campaignMobile`padding: 10px 10px 8px;`}
`;

const EmptyHint = styled.div`
  flex: 1; min-width: 0; padding: 4px 0 8px;
`;

const EmptySequenceTitle = styled.span`
  display: block; font-family: NunitoSans; font-size: 14px; font-weight: 800; color: ${styles.colors.black500};
`;

const EmptySequenceText = styled.span`
  display: block; font-family: NunitoSans; font-size: 12px; line-height: 1.5; color: ${styles.colors.black300};
  margin-top: 4px;
`;

// Templates
const TemplateGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 12px;
  ${campaignMobile`grid-template-columns: 1fr 1fr; gap: 8px;`}
`;

const TemplateCard = styled.button<{ $active: boolean }>`
  display: flex; flex-direction: column; align-items: flex-start; gap: 6px;
  padding: 16px 18px; border-radius: 14px; cursor: pointer; text-align: left;
  border: 1px solid ${({ $active }) => $active ? ACCENT : '#e1e5eb'};
  background: ${({ $active }) => $active ? 'rgba(89,100,255,0.05)' : '#fff'};
  box-shadow: ${({ $active }) => $active ? '0px 1px 3px rgba(0,0,0,0.10), 0px 1px 2px rgba(0,0,0,0.10)' : 'none'};
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
  &:hover { border-color: ${ACCENT}; background: #f8f9ff; transform: translateY(-1px); box-shadow: 0 5px 16px rgba(89,100,255,0.10); }
  ${campaignMobile`
    padding: 10px;
    &:hover { transform: none; }
  `}
`;

const TplPipeline = styled.div`display: flex; align-items: center; flex-wrap: nowrap; gap: 2px;`;

const TplEmptyIcon = styled.span`
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  width: 32px; height: 32px; border-radius: 10px;
  border: 1px dashed ${styles.colors.primary200};
  font-size: 18px; font-weight: 800; color: ${styles.colors.primary600}; line-height: 1;
`;

const TplIcon = styled.span`
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  width: 32px; height: 32px; border-radius: 10px;
  background: ${styles.colors.primary100}; border: 1px solid ${styles.colors.primary200};
  .svg { width: 16px; height: 16px; } .path { stroke: ${styles.colors.primary600}; fill: none; }
`;

const TplArrow = styled.span`font-size: 10px; color: ${styles.colors.primary400};`;

const TplName = styled.span<{ $active: boolean }>`
  font-family: NunitoSans; font-size: 14px; font-weight: 800;
  color: ${INK};
`;

const TplDesc = styled.span`
  font-family: NunitoSans; font-size: 12px; color: ${MUTED}; line-height: 1.5;
`;

// Timeline
const TimelineRow = styled.div`
  display: flex; gap: 12px; align-items: flex-start;
  ${campaignMobile`gap: 8px;`}
`;

const RailSpacer = styled.div`
  width: 26px; flex-shrink: 0;
  ${campaignMobile`width: 20px;`}
`;

const Rail = styled.div`
  display: flex; flex-direction: column; align-items: center;
  width: 26px; flex-shrink: 0; padding-top: 12px;
  ${campaignMobile`width: 20px; padding-top: 10px;`}
`;

const StepBadge = styled.div`
  width: 26px; height: 26px; border-radius: 50%;
  background: ${ACCENT}; color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-family: NunitoSans; font-size: 11px; font-weight: 800; flex-shrink: 0; z-index: 1;
  ${campaignMobile`width: 22px; height: 22px; font-size: 10px;`}
`;

const RailLine = styled.div`
  width: 1px; flex: 1; min-height: 14px; background: ${BORDER}; margin: 4px 0;
`;

const StepCard = styled.div<{ $dragging?: boolean; $dropTarget?: boolean }>`
  flex: 1; min-width: 0; border-radius: 14px;
  border: 1px solid #e1e5eb; background: #fff;
  margin-bottom: 10px; overflow: hidden;
  box-shadow: 0px 1px 3px rgba(0,0,0,0.10), 0px 1px 2px rgba(0,0,0,0.10);
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.12s ease, opacity 0.12s ease;
  opacity: ${({ $dragging }) => ($dragging ? 0.65 : 1)};
  transform: ${({ $dropTarget }) => ($dropTarget ? 'translateY(-3px)' : 'translateY(0)')};
  border-color: ${({ $dropTarget }) => ($dropTarget ? 'rgba(89,100,255,0.40)' : '#e1e5eb')};
  &:hover { border-color: #d4dbe8; box-shadow: 0 5px 18px rgba(20,30,80,0.06); }
`;

const StepHead = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #e1e5eb;
  ${campaignMobile`
    flex-direction: column;
    align-items: stretch;
    padding: 10px 12px;
    gap: 10px;
  `}
`;

const StepHeadMain = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1; min-width: 0;
`;

const StepHeadActions = styled.div`
  display: flex; align-items: center; gap: 8px;
  flex-shrink: 0;
  ${campaignMobile`
    justify-content: flex-end;
    flex-wrap: wrap;
  `}
`;

const StepIcon = styled.span`
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  width: 40px; height: 40px; border-radius: 10px;
  background: ${styles.colors.primary100};
  border: 1px solid ${styles.colors.primary200};
  .svg { width: 20px; height: 20px; } .path { stroke: ${styles.colors.primary600}; fill: none; }
`;

const StepLabel = styled.span`
  font-family: NunitoSans; font-size: 14px; font-weight: 700; color: ${INK};
`;

const StepHint = styled.span`
  font-family: NunitoSans; font-size: 12px; color: #64748b;
  line-height: 1.4;
`;

const DayBox = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  height: 32px;
  padding: 0 10px;
  background: #fff;
  border: 1px solid #e1e5eb;
  border-radius: 8px;
  box-sizing: border-box;
`;

const DayLbl = styled.span`
  font-family: NunitoSans;
  font-size: 14px;
  font-weight: 700;
  color: #64748b;
  line-height: 1;
  white-space: nowrap;
`;

const DayInput = styled.input`
  width: 40px;
  height: 28px;
  border: none;
  outline: none;
  background: transparent;
  font-family: NunitoSans;
  font-size: 14px;
  font-weight: 700;
  color: ${INK};
  text-align: right;
  box-sizing: border-box;
  padding: 0;
`;

const DayTag = styled.span`
  font-family: NunitoSans; font-size: 11px; font-weight: 600; color: ${MUTED};
  padding: 3px 9px; border-radius: 6px; background: #f1f5f9; white-space: nowrap;
`;

const DragCursorWrap = styled.span<{ $dragging?: boolean }>`
  display: inline-flex;
  cursor: ${({ $dragging }) => ($dragging ? 'grabbing' : 'grab')} !important;
  & * { cursor: inherit !important; }
`;

const DragHandle = styled.button<{ $dragging?: boolean }>`
  width: 24px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: ${({ $dragging }) => ($dragging ? 'grabbing' : 'grab')} !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  .svg { width: 16px; height: 16px; }
  .path { stroke: ${styles.colors.black300}; fill: none; }
  &:active { cursor: grabbing; }
  &:hover { background: #f8fafc; }
`;

const IconBtn = styled.button<{ $danger?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 11px; font-weight: 700;
  color: ${({ $danger }) => $danger ? '#ef4444' : MUTED};
  display: flex; align-items: center; justify-content: center;
  .svg { width: 16px; height: 16px; }
  .path {
    ${({ $danger }) => $danger ? 'stroke: #ef4444 !important;' : ''}
    fill: none !important;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  &:disabled { opacity: 0.3; cursor: default; }
  &:hover:not(:disabled) {
    background: ${({ $danger }) => $danger ? '#fef2f2' : '#f8fafc'};
  }
`;

const StepBody = styled.div`
  padding: 12px 14px; display: flex; flex-direction: column; gap: 8px;
  ${campaignMobile`padding: 10px 12px;`}
`;

const ModeRow = styled.div`
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
  ${campaignMobile`
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  `}
`;

const ModeBtn = styled.button<{ $active: boolean }>`
  height: 28px; padding: 0 12px; border-radius: 6px; cursor: pointer;
  display: inline-flex; align-items: center; gap: 5px;
  font-family: NunitoSans; font-size: 11px; font-weight: 700;
  border: 1px solid ${({ $active }) => $active ? ACCENT : BORDER};
  background: ${({ $active }) => $active ? '#f0f4ff' : '#fff'};
  color: ${({ $active }) => $active ? ACCENT : MUTED};
  transition: all 0.1s;
  .svg { width: 13px; height: 13px; flex-shrink: 0; }
  .path {
    fill: none !important;
    stroke: ${({ $active }) => $active ? ACCENT : MUTED} !important;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  &:hover {
    border-color: ${ACCENT};
    color: ${ACCENT};
    background: #f8f9ff;
    .path { stroke: ${ACCENT} !important; }
  }
  ${campaignMobile`
    width: 100%;
    justify-content: center;
    height: 32px;
  `}
`;




const ReadOnly = styled.div`
  font-family: NunitoSans; font-size: 12px; color: #475569;
  line-height: 1.6; white-space: pre-wrap; padding: 2px 0;
`;

// ─── Email step ───────────────────────────────────────────────────────────────

const FieldHint = styled.div`
  font-family: NunitoSans; font-size: 11px; color: #94a3b8;
  line-height: 1.5; margin-top: 6px;
`;

const ReadOnlySubject = styled.div`
  font-family: NunitoSans; font-size: 12px; font-weight: 700; color: #1e293b;
  margin-bottom: 6px;
`;

const ThreadRow = styled.div`
  margin-bottom: 14px;
`;

const ThreadToggle = styled.button<{ $active: boolean }>`
  display: flex; align-items: center; gap: 8px;
  border: none; background: none; padding: 0; cursor: pointer;
  font-family: NunitoSans; font-size: 12px; font-weight: 600;
  color: ${p => p.$active ? '#0f172a' : '#64748b'};
`;

const ThreadBox = styled.span<{ $active: boolean }>`
  width: 16px; height: 16px; flex: 0 0 16px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 4px;
  border: 1px solid ${p => p.$active ? ACCENT : '#cbd5e1'};
  background: ${p => p.$active ? ACCENT : '#fff'};
  svg, .svg { width: 12px; height: 12px; display: block; }
  /* The tick is drawn with fill, not stroke. */
  .path { fill: #fff; }
`;

const NoteLimitBox = styled.div`
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #fde68a;
  background: #fffbeb;
`;

const NoteLimitTitle = styled.div`
  font-family: NunitoSans; font-size: 12px; font-weight: 700; color: #92400e;
  margin-bottom: 4px;
`;

const NoteLimitText = styled.div`
  font-family: NunitoSans; font-size: 12px; color: #a16207; line-height: 1.45;
  margin-bottom: 10px;
`;

const FallbackRow = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px;
`;

const FallbackBtn = styled.button<{ $active: boolean }>`
  border: 1px solid ${p => p.$active ? '#d97706' : '#fcd34d'};
  background: ${p => p.$active ? '#fef3c7' : '#fff'};
  color: #92400e;
  border-radius: 8px;
  padding: 6px 10px;
  font-family: NunitoSans; font-size: 11px; font-weight: 600;
  cursor: pointer;
  &:hover { background: #fef3c7; }
`;

const ChoiceSection = styled.div`
  margin-top: 4px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ChoiceSectionTitle = styled.div`
  font-family: NunitoSans;
  font-size: 13px;
  font-weight: 700;
  color: #334155;
`;

const ChoiceCards = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  ${campaignMobile`grid-template-columns: 1fr;`}
`;

const ChoiceCard = styled.button<{ $active: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  text-align: left;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1.5px solid ${p => (p.$disabled ? '#e2e8f0' : p.$active ? ACCENT : BORDER)};
  background: ${p => (p.$disabled ? '#f8fafc' : p.$active ? styles.colors.primary100 : '#fff')};
  cursor: ${p => (p.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${p => (p.$disabled ? 0.72 : 1)};
  transition: border-color 0.12s ease, background 0.12s ease;
  &:hover {
    border-color: ${p => (p.$disabled ? '#e2e8f0' : p.$active ? ACCENT : '#cbd5e1')};
    background: ${p => (p.$disabled ? '#f8fafc' : p.$active ? styles.colors.primary100 : '#f8fafc')};
  }
`;

const ChoiceRadio = styled.span<{ $active: boolean }>`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-top: 2px;
  border-radius: 50%;
  border: 2px solid ${p => (p.$active ? ACCENT : '#cbd5e1')};
  background: ${p => (p.$active ? ACCENT : '#fff')};
  box-shadow: ${p => (p.$active ? `inset 0 0 0 3px #fff` : 'none')};
`;

const ChoiceCopy = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const ChoiceLabel = styled.span`
  font-family: NunitoSans;
  font-size: 13px;
  font-weight: 700;
  color: ${INK};
`;

const ChoiceHint = styled.span`
  font-family: NunitoSans;
  font-size: 12px;
  line-height: 1.45;
  color: #64748b;
`;

const ChoiceInfoNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f1f5f9;
  font-family: NunitoSans;
  font-size: 12px;
  line-height: 1.45;
  color: #64748b;
`;

const ChoiceInfoIcon = styled.span`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-top: 1px;
  border-radius: 50%;
  border: 1.5px solid #94a3b8;
  color: #64748b;
  font-size: 10px;
  font-weight: 700;
  line-height: 13px;
  text-align: center;
  font-family: NunitoSans;
`;

const TerminalIcon = styled.span`
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  width: 28px; height: 28px; border-radius: 8px; background: #eef2ff;
  .svg { width: 14px; height: 14px; } .path { stroke: #6366f1; fill: none; }
`;

const TerminalCard = styled.div`
  flex: 1; min-width: 0; display: flex; align-items: flex-start; gap: 10px;
  padding: 12px 14px; border-radius: 10px; margin-top: 10px; margin-bottom: 0;
  border: 1px solid ${BORDER}; background: #fafbfc;
  ${campaignMobile`padding: 10px 12px;`}
`;

const TerminalTitle = styled.span`
  font-family: NunitoSans; font-size: 14px; font-weight: 700; color: ${INK};
`;

const TerminalDesc = styled.span`
  font-family: NunitoSans; font-size: 12px; line-height: 1.5; color: #475569;
  strong { font-weight: 700; color: ${INK}; }
`;

const AddRow = styled.div`
  flex: 1; min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 17px;
  background: #fff;
  border: 1px dashed #e1e5eb;
  border-radius: 14px;
`;

const AddLbl = styled.span`
  font-family: NunitoSans; font-size: 10px; font-weight: 800; color: ${MUTED};
  text-transform: uppercase; letter-spacing: 0.07em; white-space: nowrap;
`;

const AddBtnsRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const AddBtnWrap = styled.span`
  display: inline-flex;
`;

const AddBtn = styled.button`
  height: 38px;
  padding: 0 13px;
  border-radius: 10px;
  cursor: pointer;
  font-family: NunitoSans;
  font-size: 13px;
  font-weight: 700;
  border: 1px solid #e1e5eb;
  background: #fff;
  color: #111a2d;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.1s;
  .svg { width: 16px; height: 16px; flex-shrink: 0; } .path { stroke: ${ACCENT}; fill: none; }
  &:hover:not(:disabled) { border-color: ${ACCENT}; color: ${ACCENT}; background: #f8f9ff; }
  &:disabled { opacity: 0.45; cursor: not-allowed;
    .path { stroke: #94a3b8; } }
  ${campaignMobile`
    flex: 1 1 calc(50% - 4px);
    justify-content: center;
    min-width: 0;
    height: 40px;
  `}
`;

// Language dropdown styled components











const ReadOnlyAiMeta = styled.div`
  display: flex; align-items: center; gap: 6px; margin-bottom: 4px;
  font-family: NunitoSans; font-size: 12px; color: #64748b;
`;

const LangBadge = styled.span`
  display: inline-flex; align-items: center; gap: 3px;
  padding: 1px 6px; border-radius: 4px;
  background: #f0f4ff; border: 1px solid #c7d2fe;
  font-family: NunitoSans; font-size: 10px; font-weight: 700; color: ${ACCENT};
`;

export default CampaignSequenceBuilder;
